import React from "react";

import { formatDateOnly } from "../../../utils/datetime";
import { chf, countText } from "./financeFormat";
import {
  selectAgingRows,
  selectReceivablesAsOf,
  selectReceivablesExcludedNotice,
} from "./financeSelectors";

/**
 * How old the open receivables are, in the backend's own seven classes.
 *
 * NOTHING HERE DECIDES WHAT IS OVERDUE. The buckets arrive classified: the
 * service compares each invoice's due date with its own as-of date and publishes
 * the result. This component orders, labels and draws them. There is no date
 * comparison, no day arithmetic and no device clock anywhere on this path —
 * which is the point, because a browser doing that arithmetic would be a second
 * definition of "overdue" running in a timezone nobody chose.
 *
 * SEVEN CLASSES, NOT FIVE. `due_today` is not folded into "1–30 Tage" — an
 * invoice due today has an age of zero days, so that label would be false — and
 * `no_due_date` is not folded into "Nicht fällig": those are real open
 * receivables whose age is UNKNOWN, which is a different statement. The order
 * and the German labels come from the constants the revenue-analytics page
 * already uses, so there is one list to keep in step with the backend.
 *
 * THE BARS ARE NOT THE INFORMATION. Every row states its label, its amount and
 * its invoice count as text; the bar is `aria-hidden` decoration scaled against
 * the largest bucket. A reader who cannot see it loses nothing, and a reader who
 * can never has to infer a figure from a length.
 */

export default function FinanceAgingCard({ revenueAnalytics, loading = false, onRetry }) {
  const ra = revenueAnalytics?.data;
  const rows = selectAgingRows(ra);
  const asOf = selectReceivablesAsOf(ra);
  const excluded = selectReceivablesExcludedNotice(ra);
  const failed = Boolean(revenueAnalytics?.error);

  return (
    <section
      data-testid="finance-aging-card"
      aria-busy={loading || undefined}
      aria-labelledby="finance-aging-heading"
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3
          id="finance-aging-heading"
          className="m-0 text-[12px] font-semibold text-card-foreground"
        >
          Altersstruktur der Forderungen
        </h3>
        <span className="text-[11px] text-muted-foreground">
          {asOf ? `Stand ${formatDateOnly(asOf)}` : "Aktueller Stand"}
        </span>
      </div>

      {failed ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {/* Text, not colour alone. */}
          <span className="text-[11px] text-destructive">Nicht verfügbar</span>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      ) : loading ? (
        <p className="mt-3 text-[11px] text-muted-foreground">
          Altersstruktur wird geladen…
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-[11px] text-muted-foreground">
          Keine Altersstruktur verfügbar.
        </p>
      ) : (
        <ul className="mt-3 space-y-2.5">
          {rows.map((row) => (
            <li key={row.key} data-testid={`finance-aging-row-${row.key}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className="text-[12px] text-card-foreground">{row.label}</span>
                <span className="text-[12px] text-card-foreground">
                  <span className="font-medium">
                    {/* A bucket the payload did not carry renders "—". It is NOT
                        shown as CHF 0: "not told" and "none" are different
                        claims, and only one of them is safe here. */}
                    {row.amount === null ? "—" : chf(row.amount)}
                  </span>
                  {row.count === null ? null : (
                    <span className="ml-2 text-[11px] text-muted-foreground">
                      {countText(row.count)} Rg.
                    </span>
                  )}
                </span>
              </div>
              <div
                aria-hidden
                className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted"
              >
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${row.share * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      {!failed && excluded.present ? (
        <p
          data-testid="finance-receivables-excluded"
          className="mt-3 border-t border-border pt-2 text-[11px] leading-snug text-muted-foreground"
        >
          {countText(excluded.count)} Rechnungen sind nicht in der
          Forderungsanalyse enthalten — nicht beim Anbieter auffindbar, ohne
          klassifizierbaren Status oder in einer anderen bzw. unbekannten
          Währung. Ihre Beträge sind in den Summen oben bewusst nicht enthalten.
        </p>
      ) : null}
    </section>
  );
}
